import React from 'react';
import { Accordion, Switch, Icon, TextField, Typography } from '@scale/scaleui';
import { Autocomplete, TextField as ScaleUIRadixTextField } from '@scale/scaleui-radix';
import { MultiTurnInteractionConfiguration, UIGenerationLanguage } from '@scale/shared/types/chat';
import ConfigSectionHeader from '../components/ConfigSectionHeader';
import { ExperimentalFeature, DeprecatedFeature } from '../components/ConfigLifeCycleInfo';
import { faSearch } from '@fortawesome/pro-regular-svg-icons';
interface CodingOptionsProps {
  value: MultiTurnInteractionConfiguration;
  onSubmit: (config: MultiTurnInteractionConfiguration) => void;
  showEnableAttachmentsSwitch?: boolean;
  useAccordion?: boolean;
}

const CodingOptions = ({
  value,
  onSubmit,
  showEnableAttachmentsSwitch,
  useAccordion = true,
}: CodingOptionsProps) => {
  const WrappingComponent = useAccordion ? Accordion : ConfigSectionHeader;
  return (
    <div className="flex flex-col gap-2">
      <Typography.H3>Coding Options</Typography.H3>
      <Typography.Paragraph2 color="text-neutral-500">
        Configure coding options such as displaying model executed code and rendering code in
        responses via regex.
      </Typography.Paragraph2>
      <WrappingComponent header={'Coding Configs'}>
        <div className="flex flex-col gap-2">
          {showEnableAttachmentsSwitch && (
            <div className="space-y-2">
              <Switch
                altDescription="Display Executed Code"
                label="Display Executed Code"
                checked={value.displayExecutedCode}
                onCheckedChange={checked => onSubmit({ ...value, displayExecutedCode: checked })}
              />
              <Typography.Paragraph2 color="text-neutral-500">
                Allow contributors to view model executed code.
              </Typography.Paragraph2>
            </div>
          )}

          {/* render code, and code regex if render code is set */}
          <div className="space-y-2">
            <Switch
              altDescription="Render Code in Model Responses"
              label="Render Code in Model Responses"
              checked={value.renderCode}
              onCheckedChange={checked => onSubmit({ ...value, renderCode: checked })}
            />
            <DeprecatedFeature
              name="Generate HTML Code on model response selector + model response editor"
              details="Add button to generate HTML code (use the individual step configs to generate html code)"
              showInfoCondition={!!value.showHtmlGen}
            >
              <Switch
                altDescription="Generate HTML Code"
                label="Add button to generate HTML code on model response selector + model response editor"
                checked={value.showHtmlGen}
                onCheckedChange={checked => onSubmit({ ...value, showHtmlGen: checked })}
              />
            </DeprecatedFeature>
            <ExperimentalFeature
              name="Languages for UI code generation"
              details="Languages for UI code generation (configured in other steps)"
              showInfoCondition={(value.uiCodeGenerationLanguages?.length ?? 0) > 0}
            >
              <Autocomplete.Root
                multiple
                value={value.uiCodeGenerationLanguages}
                onValueChange={(newUiCodeGenerationLanguages: UIGenerationLanguage[]) =>
                  onSubmit({ ...value, uiCodeGenerationLanguages: newUiCodeGenerationLanguages })
                }
              >
                <Autocomplete.Content>
                  <Autocomplete.TextField placeholder="Select languages allowed for UI code generation">
                    <ScaleUIRadixTextField.Slot side="left">
                      <Icon icon={faSearch} />
                    </ScaleUIRadixTextField.Slot>
                    <Autocomplete.Values>
                      {Object.values(UIGenerationLanguage).map(option => (
                        <Autocomplete.Value key={option} value={option}>
                          {option}
                        </Autocomplete.Value>
                      ))}
                    </Autocomplete.Values>
                  </Autocomplete.TextField>
                  <Autocomplete.Items>
                    {Object.values(UIGenerationLanguage).map(language => (
                      <Autocomplete.Item key={language} value={language}>
                        {language}
                      </Autocomplete.Item>
                    ))}
                  </Autocomplete.Items>
                </Autocomplete.Content>
              </Autocomplete.Root>
            </ExperimentalFeature>

            {value.renderCode && (
              <TextField
                id="codeRegex"
                label="Code Regex"
                placeholder="Regex to find code in model responses"
                value={value.codeRegex}
                onChange={e => onSubmit({ ...value, codeRegex: e.target.value })}
              />
            )}
          </div>
        </div>
      </WrappingComponent>
    </div>
  );
};

export default CodingOptions;
